export const EMAIL_NOTIFICATION_OPEN_EVENT = 'quantis:open-email-notification';

const STORAGE_KEY = 'quantis.pending-email-notification.v1';

export interface EmailNotificationOpenRequest {
  requestKey: string;
  notificationId?: string;
  messageId?: string;
  threadId?: string;
}

function nonEmpty(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function normalise(
  input: Partial<EmailNotificationOpenRequest> | null | undefined,
): EmailNotificationOpenRequest | null {
  const messageId = nonEmpty(input?.messageId);
  const threadId = nonEmpty(input?.threadId);
  if (!messageId && !threadId) return null;

  const notificationId = nonEmpty(input?.notificationId);
  const requestKey =
    nonEmpty(input?.requestKey) ||
    `${notificationId || messageId || threadId}:${Date.now()}:${Math.random().toString(36).slice(2)}`;

  return { requestKey, notificationId, messageId, threadId };
}

export function queueEmailNotificationOpen(
  input: Partial<EmailNotificationOpenRequest>,
): EmailNotificationOpenRequest | null {
  const request = normalise(input);
  if (!request || typeof window === 'undefined') return request;

  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(request));
  } catch {
    // The live event still opens the email when storage is unavailable.
  }
  window.dispatchEvent(new CustomEvent(EMAIL_NOTIFICATION_OPEN_EVENT, { detail: request }));
  return request;
}

export function clearPendingEmailNotificationOpen(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage may be disabled; there is nothing else to clear.
  }
}

function consumeLocationRequest(): EmailNotificationOpenRequest | null {
  if (typeof window === 'undefined' || window.location.pathname.replace(/\/+$/, '') !== '/email') {
    return null;
  }

  const params = new URLSearchParams(window.location.search);
  const request = normalise({
    notificationId: params.get('emailNotificationId') || undefined,
    messageId: params.get('emailMessageId') || undefined,
    threadId: params.get('emailThreadId') || undefined,
  });
  if (!request) return null;

  params.delete('emailNotificationId');
  params.delete('emailMessageId');
  params.delete('emailThreadId');
  const query = params.toString();
  window.history.replaceState(
    window.history.state,
    '',
    `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`,
  );
  return request;
}

export function consumePendingEmailNotificationOpen(): EmailNotificationOpenRequest | null {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (raw) {
      window.sessionStorage.removeItem(STORAGE_KEY);
      const request = normalise(JSON.parse(raw));
      if (request) return request;
    }
  } catch {
    clearPendingEmailNotificationOpen();
  }

  return consumeLocationRequest();
}
